/**
 * Parse battle video titles to extract rapper names and event name
 * Expected format: [VERBAL WAR ZONE] RAPPER1 VS RAPPER2 EVENT_NAME
 */

export const parseBattleTitle = (title) => {
  // Remove the [VERBAL WAR ZONE] prefix and normalize whitespace
  const cleaned = title
    .replace(/\[\s*VERBAL\s+WAR\s+ZONE\s*\]/i, '')
    .trim()
    .replace(/\s+/g, ' ');

  // Find "VS" (case insensitive)
  const vsMatch = cleaned.match(/\bVS\b/i);

  if (!vsMatch) {
    return {
      success: false,
      rapper1: null,
      rapper2: null,
      eventName: null,
      confidence: 0,
    };
  }

  const vsIndex = vsMatch.index;

  // Extract rapper1 (everything before VS)
  const rapper1Part = cleaned.substring(0, vsIndex).trim();

  // Extract the rest after VS
  const afterVs = cleaned.substring(vsIndex + 2).trim();

  // Try to split rapper2 and event name
  // Strategy: Find common event keywords or assume last 2-4 words are event name
  const eventKeywords = [
    'AUDIO WARFARE',
    'PURGE',
    'SNIPER DIVISION',
    'REMATCH',
    'PROVING GROUNDS',
    'TITLE MATCH',
    'CHAMPIONSHIP',
  ];

  let rapper2 = null;
  let eventName = null;
  let confidence = 50; // Base confidence

  // Try to find event keywords
  for (const keyword of eventKeywords) {
    const keywordRegex = new RegExp(`\\b${keyword}`, 'i');
    const match = afterVs.match(keywordRegex);

    if (match) {
      rapper2 = afterVs.substring(0, match.index).trim();
      eventName = afterVs.substring(match.index).trim();
      confidence = 80; // Higher confidence when we find known event
      break;
    }
  }

  // Fallback: assume last 2-3 words are event name if they contain numbers or are short
  if (!rapper2) {
    const words = afterVs.split(' ');

    if (words.length > 3) {
      // Check if last words contain numbers or look like event names
      const lastWords = words.slice(-3).join(' ');
      const hasNumber = /\d/.test(lastWords);

      if (hasNumber || words.length > 5) {
        rapper2 = words.slice(0, -3).join(' ');
        eventName = lastWords;
        confidence = 60;
      } else {
        // Take last 2 words as event
        rapper2 = words.slice(0, -2).join(' ');
        eventName = words.slice(-2).join(' ');
        confidence = 50;
      }
    } else {
      // Too short, just split in half
      rapper2 = afterVs;
      eventName = '';
      confidence = 40;
    }
  }

  return {
    success: true,
    rapper1: rapper1Part,
    rapper2: rapper2,
    eventName: eventName,
    confidence: confidence,
  };
};

/**
 * Calculate string similarity using Levenshtein distance
 * Returns a value between 0 and 1 (1 being identical)
 */
const stringSimilarity = (str1, str2) => {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();

  if (s1 === s2) return 1;

  const len1 = s1.length;
  const len2 = s2.length;

  if (len1 === 0) return len2 === 0 ? 1 : 0;
  if (len2 === 0) return 0;

  const matrix = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  const distance = matrix[len1][len2];
  const maxLen = Math.max(len1, len2);

  return 1 - distance / maxLen;
};

/**
 * Find best matching artist from database
 * Returns { artist, confidence } or null
 */
export const findBestArtistMatch = (rapperName, artists) => {
  if (!rapperName || !artists || artists.length === 0) {
    return null;
  }

  let bestMatch = null;
  let bestScore = 0;

  for (const artist of artists) {
    // Check against both display_name and username
    const displayScore = stringSimilarity(rapperName, artist.display_name || '');
    const usernameScore = stringSimilarity(rapperName, artist.username || '');

    const score = Math.max(displayScore, usernameScore);

    if (score > bestScore) {
      bestScore = score;
      bestMatch = artist;
    }
  }

  // Only return match if confidence is above threshold
  if (bestScore >= 0.6) {
    return {
      artist: bestMatch,
      confidence: Math.round(bestScore * 100),
      isExactMatch: bestScore >= 0.95,
    };
  }

  return null;
};

/**
 * Get confidence level indicator
 */
export const getConfidenceLevel = (confidence) => {
  if (confidence >= 80) return 'high';
  if (confidence >= 60) return 'medium';
  return 'low';
};

/**
 * Get confidence color for UI
 */
export const getConfidenceColor = (confidence) => {
  if (confidence >= 80) return 'text-green-500';
  if (confidence >= 60) return 'text-yellow-500';
  return 'text-red-500';
};
